import { toRaw, type Ref } from 'vue'
import type { ConvertDocumentRequest, WebChatMessage } from '~/types/web-chat'
import type { ApiFetch } from '~/composables/useApi'
import type { WorkflowActionsDeps } from '~/composables/web-chat/useWorkflowActions'
import { toolRunFailureText, toolRunModelReason, sanitizeUpstreamError } from '~/utils/toolRunMessages'
import { isTerminalToolRunStatus, type ToolRun, type ToolRunResponse } from '#shared/tools/tool'
import { useActivityRunsStore } from '~/stores/activityRuns'
import { planAllows } from '~/composables/usePlanFeature'

/**
 * Starting a document conversion proposed in chat, and waiting for it (MAR-487).
 *
 * A conversion is a TOOL run (`/api/tools/run`), not a workflow run: a different table, a different
 * status vocabulary, and no feed — so it is polled here rather than tracked by `useWorkflowRunsStore`.
 * Split out of `useWorkflowActions` (MAR-948), which composes it and still returns `acceptConversion`,
 * so the `web-chat` store's surface did not change.
 */
export interface DocumentConversionDeps {
  /** The thread — to find the message a card sits on, which the run records (MAR-991). */
  messages: Ref<WebChatMessage[]>
  apiFetch: ApiFetch
  sendMessage: WorkflowActionsDeps['sendMessage']
  /** The conversation the run is filed against, so the Activity row can link back to it. */
  conversationId?: Ref<string | null>
  /** Where the user is now — the output opens only if they are still where they started. */
  currentPath: Ref<string>
  openDocPreview: (documentId: string, name: string) => void
  /** The chat's case rung — a conversion's output files into it. */
  activeCaseId: Ref<string | null>
  /** Refreshes the document browser once the output exists. */
  refreshDocumentBrowser: () => void
}

export function useDocumentConversion(deps: DocumentConversionDeps) {
  const { messages, apiFetch, sendMessage, conversationId, currentPath, openDocPreview, activeCaseId, refreshDocumentBrowser } = deps

  /** The message this card sits on. Compared raw: the thread's elements are reactive proxies. */
  function messageIdOf(req: ConvertDocumentRequest): string | null {
    const raw = toRaw(req)
    return messages.value.find(m => m.card?.toolId === 'convert_document' && toRaw(m.card.request) === raw)?.id ?? null
  }

  // Safe here: the only caller is `useWorkflowActions`, composed in the `web-chat` store's own setup.
  const { t } = useI18n()

  /**
   * Start a proposed CONVERSION — now a TOOL RUN, not a workflow run (MAR-487).
   *
   * WHAT CHANGED AND WHY. This used to POST `/api/workflows/:converterId/run`, because the two
   * converters were seeded `workflow_definitions` rows. They are code now, so there is no definition
   * to name and no input slot to fill — the request carries a `tool_id` from a fixed union and the
   * document it reads. The `inputKey` dance (reading the first input's key off the definition so a
   * renamed slot failed visibly) is gone with the definition that had slots.
   *
   * WHAT DELIBERATELY DID NOT CHANGE. The work is still queued and still waited on rather than
   * awaited in the request: a conversion is a vision model reading every page, and services/AGENTS.md's
   * corollary is why it runs under a Cloud Tasks request instead of detached from this one. The
   * completion message keeps its "PLAIN TEXT ONLY" clause for the same reason as before — the card
   * already carries the produced document, so a model that answers with `download_documents` renders
   * a visible duplicate.
   */
  async function acceptConversion(req: ConvertDocumentRequest) {
    // Same rule as `acceptWorkflow`: every refusal says why, because a silent return from a confirm
    // button is indistinguishable from a broken button.
    if (!req.toolId || !req.documentId) {
      console.warn('[webChat] convert_document: the card has no tool or document id.')
      return
    }

    // A conversion spends the workflow allowance, so `POST /api/tools/run` needs `workflows` (MAR-1212).
    // The chat can still propose one on a plan without it; the card says so instead of asking for a
    // run the server would refuse. Nothing is sent to the model: nothing ran, and the card explains.
    if (!(await planAllows('workflows'))) {
      req.status = 'failed'
      req.error = t('tools.runFailure.planLacksWorkflows')
      return
    }

    req.status = 'running'
    req.error = null
    const startedOnPath = currentPath.value

    try {
      const started = await apiFetch<ToolRunResponse>('/api/tools/run', {
        method: 'POST',
        body: {
          tool_id: req.toolId,
          source_item_id: req.documentId,
          conversation_id: conversationId?.value ?? null,
          // What puts this card's status and result link back on the right message after a reload
          // (MAR-991) — a thread can convert the same file more than once.
          message_id: conversationId?.value ? messageIdOf(req) : null,
          case_id: activeCaseId.value,
        },
      })
      const runId = started.run.id
      req.runId = runId

      // The Activity Center is the other place this run is visible, and it must show it while it is
      // still running rather than after the fact — so the shared feed is asked for a tick now rather
      // than left to notice the run whenever it next happens to be polling (#70).
      useActivityRunsStore().ensurePoll()

      const settled = await awaitToolRunSettled(runId)
      req.status = settled.status === 'succeeded' ? 'completed' : 'failed'
      // What the finished card links to (MAR-991) — the only way back to the output once the
      // auto-opened preview is closed, or was never opened because the user had moved on.
      req.outputItemId = settled.output_item_id ?? null
      req.outputItemName = settled.output_item_name ?? null
      // Localized from the CODE, never the server's `message` — that string is an operator
      // diagnostic naming the internal tool id, and the card prints whatever it is given (MAR-491).
      if (settled.status === 'failed') {
        req.error = toolRunFailureText(t, settled.error, req.documentName)
      }

      refreshDocumentBrowser()

      // Open the output only if it exists and the user is still where they started — same rule as
      // the workflow path, which will not yank a user out of a page they navigated to meanwhile.
      if (settled.output_item_id && currentPath.value === startedOnPath) {
        // The panel must describe the CONVERTED file it actually renders, not the source the
        // request was made from — `output_item_name` is the server-joined name for
        // `output_item_id` (server/services/tools/runs.ts). It is null only if the output item
        // was deleted between settling and this call; fall back to the source name rather than
        // leaving the header blank (#237).
        openDocPreview(settled.output_item_id, settled.output_item_name || req.documentName || '')
      }

      // English, per services/agent-web-chat/AGENTS.md, but NOT the server's `message`: the next clause tells the model
      // to relay this reason, and the server's version names the internal tool id (MAR-491).
      const modelReason = toolRunModelReason(settled.error, req.documentName)
      await sendMessage(
        `[The document conversion finished. Run id: ${runId}. Status: ${settled.status}.`
        + (modelReason ? ` Reason: ${modelReason}` : '')
        + ' Review the result and tell the user what was actually produced — never claim anything the'
        + ' result does not show, and if it failed, relay the reason rather than describing it as done.'
        + ' The original file was not modified. Reply with PLAIN TEXT ONLY: do not call any tool for'
        + ' this, and specifically do not call download_documents — the card already shown to the user'
        + ' has its own download button for this exact file.]',
        undefined,
        { hiddenFromUi: true, persist: false },
      )
    }
    catch (err: any) {
      req.status = 'failed'
      // `/api/tools/run` refuses a tool/file mismatch outright now (MAR-491), and says so with a
      // `code` rather than only a sentence. A coded refusal gets the same localized copy a failed
      // RUN would get — the two are the same fact to a user, and differ only in how early we knew.
      // Anything else is treated the same as a settled run with no more specific code (#807): the
      // caught exception's own `.message` can be an upstream diagnostic verbatim (the docx-service
      // or Gemini error body) and must never reach the user or the model — only the console.
      const { userMessage, modelReason, diagnostic } = sanitizeUpstreamError(t, err, req.documentName)
      req.error = userMessage
      console.error('[webChat] convert_document: request failed:', diagnostic)
      await sendMessage(
        `[The document conversion could not be started. Reason: ${modelReason} Tell the user what`
        + ' happened, in your own words; do not invent another cause and do not retry it yourself.]',
        undefined,
        { hiddenFromUi: true, persist: false },
      )
    }
  }

  /**
   * Poll one tool run until it settles.
   *
   * A POLL, not a store subscription. `useWorkflowRunsStore` tracks `workflow_runs` and is fed by the
   * workflow run feed; a tool run is a different table with no such feed, and teaching the store a
   * second row shape to save one `setInterval` would put workflow vocabulary (`executing`,
   * `cancelled`) in front of states a tool can never be in.
   */
  async function awaitToolRunSettled(runId: string): Promise<ToolRun> {
    const INTERVAL_MS = 2000
    // The queue's own dispatch deadline is 1800s; giving up earlier would leave the card claiming a
    // failure while the run is still legitimately going.
    const DEADLINE_MS = 1800 * 1000
    const startedAt = Date.now()

    for (;;) {
      const { run } = await apiFetch<ToolRunResponse>(`/api/tools/runs/${runId}`)
      if (isTerminalToolRunStatus(run.status)) return run
      if (Date.now() - startedAt > DEADLINE_MS) {
        return { ...run, status: 'failed', error: { code: 'internal', message: 'The conversion timed out.' } }
      }
      await new Promise(resolve => setTimeout(resolve, INTERVAL_MS))
    }
  }

  return { acceptConversion }
}
